import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

/**
 * LaTeXRenderer - Advanced Component to render text with LaTeX formulas
 * 
 * Features:
 * - Renders explicit LaTeX: $formula$ (inline) and $$formula$$ (display)
 * - Auto-converts common mathematical patterns to LaTeX
 * 
 * Auto-conversion patterns:
 * - Scientific notation: 10^26, 2.7×10^8
 * - Exponents: x^2, x^n, x^{-1}
 * - Subscripts: x_1, a_n, H_2O
 * - Fractions: a/b, (a+b)/(c+d), frac(a,b)
 * - Square roots: sqrt(x), √x, √(x+y)
 * - Nth roots: cbrt(x), nthroot(3,x), ∛x
 * - Conjugates: conj(z), z*, z̄
 * - Matrices: matrix[[a,b],[c,d]], |A|, det(A)
 * - Greek letters: alpha, beta, gamma, etc.
 * - Special symbols: ×, ÷, ±, ≈, ≤, ≥, ≠, ∞, √, π, θ
 * - Trigonometric: sin, cos, tan, cot, sec, csc
 * - Logarithms: log, ln, log_2
 * - Limits, integrals, summations
 * - Vectors and arrows
 * - Set notation
 */
function LaTeXRenderer({ text, className = '' }) {
  if (!text) return null;

  // Auto-convert common patterns to LaTeX if not already marked
  const autoConvertToLatex = (content) => {
    // Skip if already has LaTeX markers
    if (content.includes('$')) {
      return content;
    }

    let converted = content;

    // ============================================
    // FRACTIONS AND DIVISIONS
    // ============================================
    
    // Pattern: frac(numerator, denominator) or frac{num}{den}
    converted = converted.replace(
      /frac\s*\(\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi,
      (match, num, den) => `$\\frac{${num.trim()}}{${den.trim()}}$`
    );
    converted = converted.replace(
      /frac\s*\{([^}]+)\}\s*\{([^}]+)\}/gi,
      (match, num, den) => `$\\frac{${num}}{${den}}$`
    );

    // Pattern: Complex fractions with parentheses (a+b)/(c+d)
    converted = converted.replace(
      /\(([^()]+)\)\s*\/\s*\(([^()]+)\)/g,
      (match, num, den) => `$\\frac{${num}}{${den}}$`
    );

    // Pattern: Simple fractions a/b (but not in dates or paths)
    converted = converted.replace(
      /(?<![\/\d])(\d+)\s*\/\s*(\d+)(?![\/\d])/g,
      (match, num, den) => `$\\frac{${num}}{${den}}$`
    );

    // Pattern: Variable fractions like x/y, a/b (single letters)
    converted = converted.replace(
      /\b([a-zA-Z])\s*\/\s*([a-zA-Z])\b/g,
      (match, num, den) => `$\\frac{${num}}{${den}}$`
    );

    // ============================================
    // SQUARE ROOTS AND NTH ROOTS
    // ============================================
    
    // Helper to convert symbols inside expressions
    const convertExprSymbols = (expr) => {
      return expr
        .replace(/×/g, ' \\times ')
        .replace(/÷/g, ' \\div ')
        .replace(/\*/g, ' \\times ')
        .trim();
    };
    
    // Pattern: sqrt(expression) or √(expression)
    converted = converted.replace(
      /sqrt\s*\(\s*([^)]+)\s*\)/gi,
      (match, expr) => `$\\sqrt{${convertExprSymbols(expr.trim())}}$`
    );
    converted = converted.replace(
      /√\s*\(\s*([^)]+)\s*\)/g,
      (match, expr) => `$\\sqrt{${convertExprSymbols(expr.trim())}}$`
    );

    // Pattern: √number or √variable (without parentheses)
    converted = converted.replace(
      /√\s*(\d+\.?\d*)/g,
      (match, num) => `$\\sqrt{${num}}$`
    );
    converted = converted.replace(
      /√\s*([a-zA-Z])/g,
      (match, variable) => `$\\sqrt{${variable}}$`
    );

    // Pattern: cbrt(x) - cube root
    converted = converted.replace(
      /cbrt\s*\(\s*([^)]+)\s*\)/gi,
      (match, expr) => `$\\sqrt[3]{${expr.trim()}}$`
    );
    
    // Pattern: ∛x or ∛(expression) - cube root symbol
    converted = converted.replace(
      /∛\s*\(\s*([^)]+)\s*\)/g,
      (match, expr) => `$\\sqrt[3]{${expr.trim()}}$`
    );
    converted = converted.replace(
      /∛\s*(\d+\.?\d*)/g,
      (match, num) => `$\\sqrt[3]{${num}}$`
    );

    // Pattern: nthroot(n, x) or root(n, x)
    converted = converted.replace(
      /(?:nthroot|root)\s*\(\s*(\d+)\s*,\s*([^)]+)\s*\)/gi,
      (match, n, expr) => `$\\sqrt[${n}]{${expr.trim()}}$`
    );

    // Pattern: ^(1/n) roots like x^(1/2), x^(1/3)
    converted = converted.replace(
      /([a-zA-Z\d]+)\s*\^\s*\(\s*1\s*\/\s*(\d+)\s*\)/g,
      (match, base, n) => n === '2' ? `$\\sqrt{${base}}$` : `$\\sqrt[${n}]{${base}}$`
    );

    // ============================================
    // EXPONENTS AND SUBSCRIPTS
    // ============================================
    
    // Pattern: Scientific notation (e.g., 2.7×10^26 or 4.3 x 10^25)
    converted = converted.replace(
      /(\d+\.?\d*)\s*[×x]\s*10\s*\^\s*\{?\s*(-?\d+)\s*\}?/gi,
      (match, mantissa, exponent) => `$${mantissa} \\times 10^{${exponent}}$`
    );

    // Pattern: Negative exponents x^-1, x^{-2}
    converted = converted.replace(
      /([a-zA-Z\d]+)\s*\^\s*\{?\s*(-\d+)\s*\}?/g,
      (match, base, exp) => `$${base}^{${exp}}$`
    );

    // Pattern: Standalone exponents with numbers (e.g., 10^26)
    converted = converted.replace(
      /(\d+\.?\d*)\s*\^\s*\{?\s*(\d+)\s*\}?/g,
      (match, base, exp) => `$${base}^{${exp}}$`
    );

    // Pattern: Simple algebraic exponents (e.g., x^2, m^3, x^n)
    converted = converted.replace(
      /\b([a-zA-Z])\s*\^\s*\{?\s*(\d+|[a-zA-Z])\s*\}?/g,
      (match, base, exp) => `$${base}^{${exp}}$`
    );

    // Pattern: Complex exponents like (x+y)^2
    converted = converted.replace(
      /\(([^()]+)\)\s*\^\s*\{?\s*(\d+|[a-zA-Z])\s*\}?/g,
      (match, base, exp) => `$(${base})^{${exp}}$`
    );

    // Pattern: Subscripts x_1, a_n, H_2O
    converted = converted.replace(
      /([a-zA-Z])\s*_\s*\{?\s*(\d+|[a-zA-Z]+)\s*\}?/g,
      (match, base, sub) => `$${base}_{${sub}}$`
    );

    // ============================================
    // CONJUGATES
    // ============================================
    
    // Pattern: conj(z) or conjugate(z)
    converted = converted.replace(
      /(?:conj|conjugate)\s*\(\s*([^)]+)\s*\)/gi,
      (match, expr) => `$\\overline{${expr.trim()}}$`
    );

    // Pattern: z* (conjugate notation)
    converted = converted.replace(
      /([a-zA-Z])\s*\*/g,
      (match, variable) => `$\\overline{${variable}}$`
    );

    // Pattern: z̄ (already has combining overline)
    converted = converted.replace(
      /([a-zA-Z])̄/g,
      (match, variable) => `$\\overline{${variable}}$`
    );

    // ============================================
    // MATRICES AND DETERMINANTS
    // ============================================
    
    // Pattern: matrix[[a,b],[c,d]] or mat[[a,b],[c,d]]
    converted = converted.replace(
      /(?:matrix|mat)\s*\[\s*\[([^\]]+)\]\s*,\s*\[([^\]]+)\]\s*\]/gi,
      (match, row1, row2) => {
        const r1 = row1.split(',').map(x => x.trim()).join(' & ');
        const r2 = row2.split(',').map(x => x.trim()).join(' & ');
        return `$\\begin{pmatrix} ${r1} \\\\ ${r2} \\end{pmatrix}$`;
      }
    );

    // Pattern: 3x3 matrix matrix[[a,b,c],[d,e,f],[g,h,i]]
    converted = converted.replace(
      /(?:matrix|mat)\s*\[\s*\[([^\]]+)\]\s*,\s*\[([^\]]+)\]\s*,\s*\[([^\]]+)\]\s*\]/gi,
      (match, row1, row2, row3) => {
        const r1 = row1.split(',').map(x => x.trim()).join(' & ');
        const r2 = row2.split(',').map(x => x.trim()).join(' & ');
        const r3 = row3.split(',').map(x => x.trim()).join(' & ');
        return `$\\begin{pmatrix} ${r1} \\\\ ${r2} \\\\ ${r3} \\end{pmatrix}$`;
      }
    );

    // Pattern: det(A) or |A| for determinants
    converted = converted.replace(
      /det\s*\(\s*([^)]+)\s*\)/gi,
      (match, expr) => `$\\det(${expr.trim()})$`
    );

    // Pattern: Absolute value |x| or magnitude |v|
    converted = converted.replace(
      /\|([^|]+)\|/g,
      (match, expr) => `$|${expr.trim()}|$`
    );

    // ============================================
    // TRIGONOMETRIC AND LOGARITHMIC FUNCTIONS
    // ============================================
    
    // Helper function to convert Greek letters in arguments
    const convertGreekInArg = (arg) => {
      const greekMap = {
        'alpha': '\\alpha', 'beta': '\\beta', 'gamma': '\\gamma', 'delta': '\\delta',
        'epsilon': '\\epsilon', 'theta': '\\theta', 'lambda': '\\lambda', 'mu': '\\mu',
        'pi': '\\pi', 'sigma': '\\sigma', 'phi': '\\phi', 'omega': '\\omega',
        'rho': '\\rho', 'tau': '\\tau', 'psi': '\\psi', 'xi': '\\xi', 'eta': '\\eta'
      };
      let result = arg;
      Object.entries(greekMap).forEach(([name, latex]) => {
        result = result.replace(new RegExp(`\\b${name}\\b`, 'gi'), latex);
      });
      return result;
    };
    
    // Pattern: sin^2(x), cos^2(x) - squared trig functions (must come before regular trig)
    const trigFunctions = ['sin', 'cos', 'tan', 'cot', 'sec', 'csc', 'sinh', 'cosh', 'tanh', 'arcsin', 'arccos', 'arctan'];
    trigFunctions.forEach(func => {
      const regex = new RegExp(`\\b${func}\\s*\\^\\s*(\\d+)\\s*\\(\\s*([^)]+)\\s*\\)`, 'gi');
      converted = converted.replace(regex, (match, power, arg) => `$\\${func}^{${power}}(${convertGreekInArg(arg.trim())})$`);
    });

    // Pattern: sin(x), cos(x), tan(x), etc. - only if not already converted
    trigFunctions.forEach(func => {
      // Match trig function only if not preceded by backslash (not already converted)
      const regex = new RegExp(`(?<!\\\\)\\b${func}\\s*\\(\\s*([^)]+)\\s*\\)`, 'gi');
      converted = converted.replace(regex, (match, arg) => `$\\${func}(${convertGreekInArg(arg.trim())})$`);
    });

    // Pattern: ln(x), log(x), log_2(x), log_10(x)
    converted = converted.replace(
      /\bln\s*\(\s*([^)]+)\s*\)/gi,
      (match, arg) => `$\\ln(${arg.trim()})$`
    );
    converted = converted.replace(
      /\blog\s*_\s*(\d+)\s*\(\s*([^)]+)\s*\)/gi,
      (match, base, arg) => `$\\log_{${base}}(${arg.trim()})$`
    );
    converted = converted.replace(
      /\blog\s*\(\s*([^)]+)\s*\)/gi,
      (match, arg) => `$\\log(${arg.trim()})$`
    );

    // ============================================
    // LIMITS, INTEGRALS, SUMMATIONS
    // ============================================
    
    // Pattern: lim(x->a, f(x)) or limit as x approaches a
    converted = converted.replace(
      /lim\s*\(\s*([a-zA-Z])\s*->\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi,
      (match, variable, approaches, expr) => `$\\lim_{${variable} \\to ${approaches.trim()}} ${expr.trim()}$`
    );

    // Pattern: sum(i=1, n, expr) or sum_{i=1}^{n}
    converted = converted.replace(
      /sum\s*\(\s*([a-zA-Z])\s*=\s*(\d+)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi,
      (match, variable, start, end, expr) => `$\\sum_{${variable}=${start}}^{${end.trim()}} ${expr.trim()}$`
    );

    // Pattern: integral(a, b, f(x)dx) or int(a, b, expr)
    converted = converted.replace(
      /(?:integral|int)\s*\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi,
      (match, lower, upper, expr) => `$\\int_{${lower.trim()}}^{${upper.trim()}} ${expr.trim()}$`
    );

    // Pattern: product or prod(i=1, n, expr)
    converted = converted.replace(
      /(?:product|prod)\s*\(\s*([a-zA-Z])\s*=\s*(\d+)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi,
      (match, variable, start, end, expr) => `$\\prod_{${variable}=${start}}^{${end.trim()}} ${expr.trim()}$`
    );

    // ============================================
    // VECTORS AND ARROWS
    // ============================================
    
    // Pattern: vec(A) or vector(A)
    converted = converted.replace(
      /(?:vec|vector)\s*\(\s*([^)]+)\s*\)/gi,
      (match, expr) => `$\\vec{${expr.trim()}}$`
    );

    // Pattern: ->A or A-> for arrows
    converted = converted.replace(
      /->([a-zA-Z])/g,
      (match, letter) => `$\\vec{${letter}}$`
    );

    // Pattern: rightarrow, leftarrow, etc.
    converted = converted.replace(/rightarrow/gi, '$\\rightarrow$');
    converted = converted.replace(/leftarrow/gi, '$\\leftarrow$');
    converted = converted.replace(/leftrightarrow/gi, '$\\leftrightarrow$');
    converted = converted.replace(/->/g, '$\\rightarrow$');
    converted = converted.replace(/<-/g, '$\\leftarrow$');
    converted = converted.replace(/<->/g, '$\\leftrightarrow$');

    // ============================================
    // SET NOTATION
    // ============================================
    
    // Pattern: Set operations
    converted = converted.replace(/\\union|∪/gi, '$\\cup$');
    converted = converted.replace(/\\intersection|∩/gi, '$\\cap$');
    converted = converted.replace(/\\subset|⊂/gi, '$\\subset$');
    converted = converted.replace(/\\superset|⊃/gi, '$\\supset$');
    converted = converted.replace(/\\in|∈/gi, '$\\in$');
    converted = converted.replace(/\\notin|∉/gi, '$\\notin$');
    converted = converted.replace(/\\emptyset|∅/gi, '$\\emptyset$');
    converted = converted.replace(/\\forall|∀/gi, '$\\forall$');
    converted = converted.replace(/\\exists|∃/gi, '$\\exists$');

    // ============================================
    // GREEK LETTERS
    // ============================================
    
    const greekLetters = {
      'alpha': '\\alpha', 'beta': '\\beta', 'gamma': '\\gamma', 'delta': '\\delta',
      'epsilon': '\\epsilon', 'zeta': '\\zeta', 'eta': '\\eta', 'theta': '\\theta',
      'iota': '\\iota', 'kappa': '\\kappa', 'lambda': '\\lambda', 'mu': '\\mu',
      'nu': '\\nu', 'xi': '\\xi', 'omicron': 'o', 'pi': '\\pi',
      'rho': '\\rho', 'sigma': '\\sigma', 'tau': '\\tau', 'upsilon': '\\upsilon',
      'phi': '\\phi', 'chi': '\\chi', 'psi': '\\psi', 'omega': '\\omega',
      'Gamma': '\\Gamma', 'Delta': '\\Delta', 'Theta': '\\Theta', 'Lambda': '\\Lambda',
      'Xi': '\\Xi', 'Pi': '\\Pi', 'Sigma': '\\Sigma', 'Phi': '\\Phi',
      'Psi': '\\Psi', 'Omega': '\\Omega'
    };
    
    Object.entries(greekLetters).forEach(([name, latex]) => {
      const regex = new RegExp(`\\b${name}\\b`, 'g');
      converted = converted.replace(regex, `$${latex}$`);
    });

    // ============================================
    // SPECIAL MATH SYMBOLS AND CHARACTERS
    // ============================================
    
    // Note: Only convert symbols that are NOT already inside $...$
    // Common math symbols - use a function to avoid nested $ markers
    const convertSymbolsOutsideMath = (text, symbol, latex) => {
      // Split by $ markers
      const parts = text.split(/(\$[^$]+\$)/);
      return parts.map((part, i) => {
        // Even indices are outside math markers
        if (i % 2 === 0) {
          return part.replace(new RegExp(symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), `$${latex}$`);
        }
        return part;
      }).join('');
    };
    
    converted = convertSymbolsOutsideMath(converted, '×', '\\times');
    converted = convertSymbolsOutsideMath(converted, '÷', '\\div');
    converted = convertSymbolsOutsideMath(converted, '±', '\\pm');
    converted = convertSymbolsOutsideMath(converted, '∓', '\\mp');
    converted = convertSymbolsOutsideMath(converted, '≈', '\\approx');
    converted = convertSymbolsOutsideMath(converted, '≡', '\\equiv');
    converted = convertSymbolsOutsideMath(converted, '≤', '\\leq');
    converted = convertSymbolsOutsideMath(converted, '≥', '\\geq');
    converted = convertSymbolsOutsideMath(converted, '≠', '\\neq');
    converted = convertSymbolsOutsideMath(converted, '∞', '\\infty');
    converted = convertSymbolsOutsideMath(converted, '∂', '\\partial');
    converted = convertSymbolsOutsideMath(converted, '∇', '\\nabla');
    converted = convertSymbolsOutsideMath(converted, '∆', '\\Delta');
    converted = convertSymbolsOutsideMath(converted, '∝', '\\propto');
    converted = convertSymbolsOutsideMath(converted, '°', '^{\\circ}');
    converted = convertSymbolsOutsideMath(converted, '′', '^{\\prime}');
    converted = convertSymbolsOutsideMath(converted, '″', '^{\\prime\\prime}');

    // Pattern: Angle notation ∠ABC
    converted = converted.replace(/∠/g, '$\\angle$');

    // Pattern: Parallel and perpendicular
    converted = converted.replace(/∥|parallel/gi, '$\\parallel$');
    converted = converted.replace(/⊥|perpendicular/gi, '$\\perp$');

    // Pattern: Therefore and because
    converted = converted.replace(/∴|therefore/gi, '$\\therefore$');
    converted = converted.replace(/∵|because/gi, '$\\because$');

    // Pattern: Plus-minus expressions
    converted = converted.replace(/\+-/g, '$\\pm$');
    converted = converted.replace(/-\+/g, '$\\mp$');

    // ============================================
    // DIMENSIONAL FORMULAS (Physics)
    // ============================================
    
    // Pattern: [MLT^-1] or [MLT?1] or [M^1L^2T^-2]
    converted = converted.replace(
      /\[([MLTIAQ]+(?:\^-?\d+)?)+\]/gi,
      (match) => {
        let inner = match.slice(1, -1);
        inner = inner.replace(/\^(-?\d+)/g, '^{$1}');
        inner = inner.replace(/\?(-?\d+)/g, '^{$1}');
        return `$[${inner}]$`;
      }
    );

    // ============================================
    // CHEMISTRY NOTATION
    // ============================================
    
    // Pattern: Chemical formulas like H2O, CO2, H2SO4
    converted = converted.replace(
      /\b([A-Z][a-z]?)(\d+)/g,
      (match, element, count) => `$${element}_{${count}}$`
    );

    // Pattern: Chemical reactions arrow
    converted = converted.replace(/⇌/g, '$\\rightleftharpoons$');
    converted = converted.replace(/⇒/g, '$\\Rightarrow$');

    // ============================================
    // EQUATIONS AND RELATIONS
    // ============================================
    
    // Pattern: Simple equations like E = mc^2, F = ma
    converted = converted.replace(
      /\b([A-Z])\s*=\s*([a-zA-Z])([a-zA-Z])\^(\d+)\b/g,
      (match, lhs, m1, m2, exp) => `$${lhs} = ${m1}${m2}^{${exp}}$`
    );

    // Pattern: Proportionality x ∝ y or x proportional to y
    converted = converted.replace(
      /\b([a-zA-Z])\s*proportional\s*to\s*([a-zA-Z])\b/gi,
      (match, a, b) => `$${a} \\propto ${b}$`
    );

    // ============================================
    // CLEANUP: Merge adjacent LaTeX markers
    // ============================================
    
    // Merge adjacent $ markers (e.g., $a$$b$ → $a b$)
    converted = converted.replace(/\$\s*\$/g, ' ');
    
    // Clean up any double spaces created
    converted = converted.replace(/  +/g, ' ');

    return converted;
  };

  const processedText = autoConvertToLatex(text);

  // Split text by display math ($$...$$) and inline math ($...$)
  const renderContent = (content) => {
    const parts = [];
    let currentText = content;
    let key = 0;

    while (currentText.length > 0) {
      // Check for display math $$...$$
      const displayMathMatch = currentText.match(/\$\$(.*?)\$\$/);
      if (displayMathMatch) {
        const beforeMath = currentText.substring(0, displayMathMatch.index);
        const mathContent = displayMathMatch[1];
        
        // Process inline math in text before display math
        if (beforeMath) {
          parts.push(...processInlineMath(beforeMath, key));
          key += beforeMath.split('$').length;
        }
        
        // Add display math
        try {
          parts.push(
            <div key={`block-${key++}`} className="my-4">
              <BlockMath math={mathContent} />
            </div>
          );
        } catch (error) {
          console.error('LaTeX rendering error:', error);
          parts.push(
            <span key={`error-${key++}`} className="text-red-600">
              [LaTeX Error: {mathContent}]
            </span>
          );
        }
        
        currentText = currentText.substring(displayMathMatch.index + displayMathMatch[0].length);
      } else {
        // No more display math, process remaining text for inline math
        parts.push(...processInlineMath(currentText, key));
        break;
      }
    }

    return parts;
  };

  const processInlineMath = (text, startKey) => {
    const parts = [];
    const segments = text.split('$');
    
    segments.forEach((segment, index) => {
      if (index % 2 === 0) {
        // Regular text
        if (segment) {
          // Preserve line breaks
          const lines = segment.split('\n');
          lines.forEach((line, lineIndex) => {
            if (line) {
              parts.push(
                <span key={`text-${startKey}-${index}-${lineIndex}`}>{line}</span>
              );
            }
            if (lineIndex < lines.length - 1) {
              parts.push(<br key={`br-${startKey}-${index}-${lineIndex}`} />);
            }
          });
        }
      } else {
        // Inline math
        if (segment.trim()) {
          try {
            parts.push(
              <InlineMath key={`inline-${startKey}-${index}`} math={segment} />
            );
          } catch (error) {
            console.error('LaTeX rendering error:', error);
            parts.push(
              <span key={`error-${startKey}-${index}`} className="text-red-600">
                [LaTeX Error: {segment}]
              </span>
            );
          }
        }
      }
    });

    return parts;
  };

  return (
    <div className={`latex-content ${className}`}>
      {renderContent(processedText)}
    </div>
  );
}

export default LaTeXRenderer;
